import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { limit, query } from "firebase/firestore";
import {
  Alert,
  Box,
  Button,
  InputAdornment,
  MenuItem,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { PRODUCTS } from "../../config";
import { downloadCsv, todayStamp, toCsv } from "../../lib/licenses";
import { CUSTOMER_CSV_COLUMNS, monthBuckets } from "../../lib/customers";
import {
  SEARCH_LIMIT,
  customerCountQuery,
  customerSearchQuery,
  customersQuery,
  distributorFromOwnerFilter,
  issuedKeysCountQuery,
  issuedKeysFlagCountQuery,
  ownerFromFilter,
} from "../../lib/queries";
import { matchesAllWords, primaryToken, searchWords } from "../../lib/search";
import {
  describeQueryError,
  fetchAll,
  useCounts,
  usePagedQuery,
} from "../../hooks/usePaged";
import { useLiveQuery } from "../../hooks/useLive";
import {
  Loading,
  PageHeader,
  SectionTitle,
  StatCard,
  StatGrid,
} from "../../components/ui";
import CustomerDialog from "../../components/CustomerDialog";
import CustomerTable from "../../components/CustomerTable";
import { pagingProps } from "../../components/LicenseTable";
import MonthlyColumns from "../../components/MonthlyColumns";
import GenerateKeysDialog from "./GenerateKeysDialog";
import { useAdminData } from "./AdminData";

const PAGE_SIZE = 25;

// Trend and KPI figures, all counted by Firestore. `filter`: "all" |
// "direct" | distributorId.
function useCustomerFigures(filter, refresh) {
  const owner = ownerFromFilter(filter);
  const keyFilter = distributorFromOwnerFilter(filter);
  const months = useMemo(() => monthBuckets(12), []);
  const defs = {
    records: () => customerCountQuery(owner),
    newThisMonth: () => customerCountQuery(owner, months[11].start),
  };
  months.forEach((m) => {
    defs[`customers|${m.key}`] = () =>
      customerCountQuery(owner, m.start, m.end);
  });
  PRODUCTS.forEach((p) => {
    months.forEach((m) => {
      defs[`keys|${p.id}|${m.key}`] = () =>
        issuedKeysCountQuery(p.id, keyFilter, m.start, m.end);
    });
    defs[`issued|${p.id}`] = () =>
      issuedKeysFlagCountQuery(p.id, keyFilter, false);
    defs[`linked|${p.id}`] = () =>
      issuedKeysFlagCountQuery(p.id, keyFilter, true);
  });
  const { counts, loading, error } = useCounts(
    `customers|${filter}|${refresh}`,
    defs,
  );
  const sumProducts = (name) =>
    PRODUCTS.reduce((sum, p) => sum + (counts[`${name}|${p.id}`] || 0), 0);
  const issued = sumProducts("issued");
  return {
    loading,
    error,
    records: counts.records,
    newThisMonth: counts.newThisMonth,
    keysThisMonth: PRODUCTS.reduce(
      (sum, p) => sum + (counts[`keys|${p.id}|${months[11].key}`] || 0),
      0,
    ),
    issued,
    linkedShare: issued
      ? Math.round((sumProducts("linked") / issued) * 100)
      : 0,
    customerTrend: months.map((m) => ({
      ...m,
      count: counts[`customers|${m.key}`] || 0,
    })),
    keyTrend: months.map((m) => ({
      ...m,
      count: PRODUCTS.reduce(
        (sum, p) => sum + (counts[`keys|${p.id}|${m.key}`] || 0),
        0,
      ),
    })),
  };
}

// Customer records kept by distributors, plus LiFE's own direct customers
// (distributorId null), with monthly trends.
export default function CustomersPage() {
  const { distributors, distributorById } = useAdminData();
  const [params, setParams] = useSearchParams();
  const filter = params.get("distributor") || "all";
  const owner = ownerFromFilter(filter);
  const [input, setInput] = useState("");
  const [term, setTerm] = useState("");
  const [openId, setOpenId] = useState(null); // customer id | "new" | null
  const [generateFor, setGenerateFor] = useState(null);
  const [exporting, setExporting] = useState(null);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setTerm(input), 300);
    return () => clearTimeout(timer);
  }, [input]);

  const setFilter = (value) =>
    setParams(value === "all" ? {} : { distributor: value }, { replace: true });
  const distributorName = (id) =>
    id ? distributorById[id]?.name || "—" : "LiFE direct";

  const figures = useCustomerFigures(filter, refresh);
  const words = searchWords(term);
  const token = primaryToken(words);
  const searching = !!token;
  const paged = usePagedQuery(
    searching ? null : `customers|${filter}`,
    () => customersQuery(owner),
    {
      pageSize: PAGE_SIZE,
    },
  );
  const found = useLiveQuery(
    searching ? `customers|${filter}|${token}` : null,
    () => query(customerSearchQuery(owner, token), limit(SEARCH_LIMIT)),
  );
  const searchRows = useMemo(
    () => found.data.filter((c) => matchesAllWords(c, words)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [found.data, words.join()],
  );
  const rows = searching ? searchRows : paged.rows;
  const error = (searching ? found.error : paged.error) || figures.error;
  const open =
    openId && openId !== "new" ? rows.find((c) => c.id === openId) : null;

  async function exportRows() {
    setExporting(0);
    try {
      const all = searching
        ? searchRows
        : await fetchAll(customersQuery(owner), { onProgress: setExporting });
      downloadCsv(
        `customers-${todayStamp()}.csv`,
        toCsv(all, CUSTOMER_CSV_COLUMNS(distributorName)),
      );
    } catch (e) {
      console.error(e);
    } finally {
      setExporting(null);
    }
  }

  const figure = (value) =>
    figures.loading ? "…" : (value ?? 0).toLocaleString();

  return (
    <>
      <PageHeader
        eyebrow="Partners"
        title="Customers"
        subtitle="Customer records distributors keep for the keys they issue (optional for them), and LiFE's own direct customers."
        actions={
          <>
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<DownloadIcon />}
              disabled={exporting !== null || !rows.length}
              onClick={exportRows}
              sx={{ borderColor: "divider" }}
            >
              {exporting !== null ? `Exporting ${exporting}…` : "Export"}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenId("new")}
            >
              Add direct customer
            </Button>
          </>
        }
      />

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 2.5 }}>
        <TextField
          select
          label="Distributor"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="all">All customers</MenuItem>
          <MenuItem value="direct">LiFE direct</MenuItem>
          {distributors.map((d) => (
            <MenuItem key={d.id} value={d.id}>
              {d.name}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {describeQueryError(error)}
        </Alert>
      )}

      <StatGrid min={180}>
        <StatCard label="Customer records" value={figure(figures.records)} />
        <StatCard label="New this month" value={figure(figures.newThisMonth)} />
        <StatCard
          label="Keys to customers this month"
          value={figure(figures.keysThisMonth)}
        />
        <StatCard
          label="Keys with a customer record"
          value={figures.loading ? "…" : `${figures.linkedShare}%`}
          hint={`of ${figures.issued.toLocaleString()} keys that went to customers`}
        />
      </StatGrid>

      <Box
        sx={{
          display: "grid",
          gap: 2,
          mt: 2,
          gridTemplateColumns: { md: "1fr 1fr" },
        }}
      >
        <MonthlyColumns
          title="New customers per month"
          data={figures.customerTrend}
          unit="new customers"
        />
        <MonthlyColumns
          title="Keys to customers per month"
          data={figures.keyTrend}
          unit="keys"
        />
      </Box>

      <SectionTitle
        action={
          <TextField
            placeholder="Search customers…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            sx={{ width: { xs: 170, sm: 280 } }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
        }
      >
        Records
      </SectionTitle>
      {(searching ? found.loading : paged.loading) && !rows.length ? (
        <Loading />
      ) : (
        <CustomerTable
          rows={rows}
          onRowClick={(c) => setOpenId(c.id)}
          distributorName={distributorName}
          resetKey={`${filter}|${token}`}
          paging={
            searching
              ? undefined
              : pagingProps(paged, PAGE_SIZE, figures.records)
          }
          emptyText={
            searching
              ? "No customers match this search."
              : "No customer records yet. Distributors add them as they issue keys."
          }
        />
      )}

      <CustomerDialog
        open={openId === "new" || !!open}
        customer={open}
        owners={distributors}
        distributorId={filter !== "all" && filter !== "direct" ? filter : null}
        distributorName={open ? distributorName(open.distributorId) : null}
        onIssueKey={
          open && !open.distributorId ? (c) => setGenerateFor(c) : undefined
        }
        issueLabel="Generate key for this customer"
        onClose={() => {
          setOpenId(null);
          setRefresh((n) => n + 1);
        }}
      />
      <GenerateKeysDialog
        open={!!generateFor}
        initialCustomer={generateFor}
        onClose={() => setGenerateFor(null)}
      />
    </>
  );
}
